import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import moment from 'moment';

import { withTheme } from '@material-ui/core/styles';
import { withStyles } from '@material-ui/styles';

import { getFormat, makeGraph } from 'lib/commonLib';
import { timeGraph } from 'lib/api/time';

import CurrentStatus from 'components/atoms/CurrentStatus';
import InfoTitleArea from 'components/molecules/MbTable/InfoTitleArea';
import MultiplexChart from 'components/organisms/Chart/MultiplexChart';
import SwitchingArea from 'components/organisms/SwitchingArea';

const styles = (theme) => ({
  graphWrap: {
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    '& > div': {
      width: '49%',
      marginBottom: 5,
    },
  },
});

const graphList = [
  {
    data: {
      bar: ['ADVRTS_AMT', 'EXPT_ADVRTS_AMT'],
      line: ['SESSION_ROAS', 'ROAS', 'CONV_RATE'],
    },
    yAxisData: {
      left: { ADVRTS_AMT: true, EXPT_ADVRTS_AMT: true },
      right: {
        SESSION_ROAS: { isPercent: true },
        ROAS: { isPercent: true },
        CONV_RATE: { isPercent: true },
        individually: true,
      },
    },
    customizeData: {
      EXPT_ADVRTS_AMT: {
        color: '#434348',
      },
      ADVRTS_AMT: { color: '#7cb5ec' },
      SESSION_ROAS: {
        isPercent: true,
        color: '#90ed7d',
      },
      CONV_RATE: {
        tooltipFormat: '0.00',
        isPercent: true,
        color: '#8085e9',
      },
      ROAS: {
        isPercent: true,
        color: '#f7a35c',
      },
    },
  },
];

class StatisticsGraph extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      graphData: [],
      graphComponent: {},
      infoMessage: '',
      selectedIdx: 0,
      loading: false,
      error: false,
    };
  }

  getData = (type) => {
    const { params, locale, t, handleGraphStatus } = this.props;
    const { sDate, eDate } = params;
    const format = getFormat(locale);
    this.setState({
      ...this.state,
      loading: true,
      error: false,

      infoMessage: t('까지의 통계 그래프입니다.', {
        date: t('term', {
          startDate: moment(sDate).format(format),
          endDate: moment(eDate).format(format),
        }),
      }),
    });
    timeGraph({
      ...params,
      type: type,
    })
      .then((response) => {
        const { data } = response.data;

        this.setState({
          ...this.state,
          loading: false,
          error: false,
          graphData: data,
        });
        for (let weekType in data) {
          if (weekType === 'max') continue;
          for (let pltfomTpCode in data[weekType]) {
            const primitiveData = data[weekType][pltfomTpCode];

            this.setGraphData(
              graphList[0],
              pltfomTpCode,
              weekType,
              primitiveData,
              data['max']
            );
          }
        }
        handleGraphStatus('success');
      })
      .catch((error) => {
        this.setState({
          ...this.state,
          loading: false,
          error: true,
        });
        handleGraphStatus('error');
      });
  };

  componentDidMount() {
    const { graphType } = this.props;

    this.getData(graphType);
  }

  componentDidUpdate(prevProps) {
    const { params: prevParams } = prevProps;
    const { params } = this.props;

    if (JSON.stringify(prevParams) !== JSON.stringify(params)) {
      const { graphType } = this.props;
      this.getData(graphType);
    }
  }

  /**
   * 새로고침
   */
  handleRefesh = () => {
    const { graphType } = this.props;
    this.getData(graphType);
  };

  // 시간 반환
  makeLabel = (onlyDay) => (originalDate) => {
    const time = originalDate;

    return onlyDay ? `${time}` : <span>{`${time}`}</span>;
  };

  xAxisTick = (tickProps) => {
    const { x, y, payload } = tickProps;
    const { value } = payload;
    const timeStr = this.makeLabel(true)(value);

    return (
      <text x={x} y={y + 10} textAnchor="middle">
        {timeStr}
      </text>
    );
  };

  setGraphData = (
    { title, data, yAxisData, customizeData: customize, isPercent = false },
    pltfomTpCode,
    type,
    primitiveData,
    max
  ) => {
    const { t } = this.props;
    if (typeof data !== 'undefined') {
      const { graphData, dataKeys, customizeData, keyLabel, stackId } =
        makeGraph({
          data,
          customize,
          primitiveData,
        });

      let label;
      if (pltfomTpCode === '01') {
        label = t('온라인');
      } else {
        label = t('모바일');
      }

      if (type === '0') {
        label = t('일요일') + '(' + label + ')';
      } else if (type === '1') {
        label = t('월요일') + '(' + label + ')';
      } else if (type === '2') {
        label = t('화요일') + '(' + label + ')';
      } else if (type === '3') {
        label = t('수요일') + '(' + label + ')';
      } else if (type === '4') {
        label = t('목요일') + '(' + label + ')';
      } else if (type === '5') {
        label = t('금요일') + '(' + label + ')';
      } else if (type === '6') {
        label = t('토요일') + '(' + label + ')';
      } else if (type === 'weekday') {
        label = label + ' ' + t('평일');
      } else if (type === 'weekend') {
        label = label + ' ' + t('주말');
      }
      const result = (
        <MultiplexChart
          width="100%"
          xAxisTick={this.xAxisTick}
          makeLabel={this.makeLabel(false)}
          data={graphData}
          dataKeys={dataKeys}
          customizeData={customizeData}
          keyLabel={keyLabel}
          stackId={stackId.length !== 0 ? stackId.join('$') : null}
          yAxisData={yAxisData}
          legend
          title={label}
          isPercent={isPercent}
          maxData={max}
          useTitle={true}
          key={title}
        />
      );
      const { graphComponent } = this.state;

      graphComponent[pltfomTpCode] = {
        ...graphComponent[pltfomTpCode],
        [type]: result,
      };
      this.setState({
        ...this.state,
        graphComponent: graphComponent,
      });
    }
  };

  handleChangeGraph = (item, index) => (e) => {
    const { graphData } = this.state;

    if (typeof graphData !== 'undefined') {
      for (let weekType in graphData) {
        if (weekType === 'max') continue;
        for (let pltfomTpCode in graphData[weekType]) {
          const primitiveData = graphData[weekType][pltfomTpCode];
          this.setGraphData(
            item,
            pltfomTpCode,
            weekType,
            primitiveData,
            graphData['max']
          );
        }
      }

      this.setState({
        ...this.state,
        selectedIdx: index,
      });
    }
  };

  render() {
    const { handleRefesh } = this;
    const { classes, switchDatas, t, graphType } = this.props;
    const { infoMessage, graphComponent, loading, error } = this.state;
    let pltfomTpCodes = ['01', '02'];

    return (
      <>
        <SwitchingArea
          title={
            <>
              <InfoTitleArea
                title={
                  graphType === '01'
                    ? t('주중/주말')
                    : t('요일별') + ' ' + t('통계')
                }
                tooltipTitle={infoMessage}
                iconType="fainfoCircle"
                iconColor="graph"
              />
            </>
          }
          handleRefesh={handleRefesh}
        >
          {loading || error ? (
            <CurrentStatus type={loading ? 'loading' : error && 'error'} />
          ) : (
            <>
              {switchDatas.map((type, i) => {
                return (
                  <div className={classes.graphWrap} key={i}>
                    {pltfomTpCodes.map((pltfomTpCode) => {
                      if (typeof graphComponent[pltfomTpCode] === 'undefined')
                        return null;
                      return (
                        <div key={`${type}_${pltfomTpCode}`}>
                          {typeof graphComponent[pltfomTpCode][type] !==
                          'undefined'
                            ? graphComponent[pltfomTpCode][type]
                            : ''}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
        </SwitchingArea>
      </>
    );
  }
}

export default withTranslation()(
  connect(
    ({ locale }) => ({
      locale: locale.locale,
    }),
    null
  )(withStyles(styles)(withTheme(StatisticsGraph)))
);
